'use client';

import type { ReactNode } from 'react';
import { forwardRef } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { useTranslation } from 'next-i18next/pages';

import type { MantineSize } from '@highlight-cp/core-ui';
import {
  Box,
  CloseButton,
  InputBase,
  Menu,
  SELECT_TRANSITION_PROPS,
  Text,
} from '@highlight-cp/core-ui';

import classes from './cascading-select.module.css';

// Always below the field: a flipped list opens over the rows above it. It scrolls within the room
// below instead, which `size` publishes for `.list`.
const MIDDLEWARES = {
  flip: false,
  shift: true,
  size: {
    padding: 8,
    apply: ({
      availableHeight,
      elements,
    }: {
      availableHeight: number;
      elements: { floating: HTMLElement };
    }) => {
      elements.floating.style.setProperty(
        '--cascading-select-available-height',
        `${availableHeight}px`,
      );
    },
  },
};

export interface CascadingSelectItem {
  /** Shown in its panel, and in the input once selected. */
  label: string;
  /** What `onChange` fires with. */
  value: string;
  disabled?: boolean;
}

/**
 * A top-level entry: a group (has `items`, revealed beside it on hover or ArrowRight) or a
 * directly selectable leaf (has `value`). A union rather than two optional fields, so the
 * in-between shapes — both, neither, a leaf with an empty `items` — are unrepresentable and
 * classification cannot drift between the selected-label lookup and the render.
 */
export type CascadingSelectEntry =
  | (CascadingSelectItem & { items?: never })
  | {
      /** Shown in the top-level list. */
      label: string;
      /** The entries revealed beside this one on hover or ArrowRight. */
      items: CascadingSelectItem[];
      value?: never;
      disabled?: boolean;
    };

export interface CascadingSelectProps {
  /** Top-level entries: groups (with `items`) and directly selectable leaves (with `value`). */
  data: CascadingSelectEntry[];
  /** The selected item's value, or null before a selection. */
  value: string | null;
  /** Called with the clicked item's value, or null when the selection is cleared. */
  onChange: (value: string | null) => void;
  label?: ReactNode;
  /** Shown in the input until a selection is made. */
  placeholder?: string;
  /** Swaps the chevron for a clear button while a value is selected; clearing fires `onChange(null)`. */
  clearable?: boolean;
  disabled?: boolean;
  error?: ReactNode;
  size?: MantineSize;
  dataTestId?: string;
}

const findSelectedLabel = (
  data: CascadingSelectEntry[],
  value: string | null,
): string | undefined => {
  if (value === null) {
    return undefined;
  }
  for (const entry of data) {
    // Same discriminator as the render: an entry is a group exactly when it has `items`.
    const candidates = entry.items ?? [entry];
    const match = candidates.find((item) => item.value === value);
    if (match) {
      return match.label;
    }
  }
  return undefined;
};

// Selection is a radio item so the current value shows checked — and reads as checked — when the
// menu reopens, wherever it sits in the tree. The group's `onChange` does the firing; the item
// keeps the wrapper's close-on-click default.
const renderItem = (item: CascadingSelectItem) => (
  // Keys are namespaced against the group keys below: labels and values share no uniqueness
  // contract, so a leaf's value must not be able to collide with a group's label.
  <Menu.RadioItem key={`leaf-${item.value}`} disabled={item.disabled} value={item.value}>
    {item.label}
  </Menu.RadioItem>
);

/**
 * ### CascadingSelect
 *
 * @description
 * A select-style input whose dropdown is a two-level cascade: the top level lists groups, and
 * hovering (or arrowing into) a group opens a panel beside it with that group's items. Clicking
 * an item — or a top-level entry that has no `items` — selects it: the input shows its label, the
 * menu closes, and `onChange` fires with its value. The current selection renders checked when the
 * menu reopens. Built on the Menu's sub-menu support, so the whole tree is keyboard accessible
 * (arrows walk the groups, ArrowRight/Enter open a panel, Escape closes) with the correct menu
 * roles.
 *
 * Controlled only: pass `value` (null before a selection) and `onChange`. The component is
 * generic over `data` — it knows nothing about what the entries mean.
 *
 * @example
 * ```tsx
 * <CascadingSelect
 *   label={t('Category')}
 *   placeholder={t('Select')}
 *   data={[
 *     { label: 'Personal', items: [{ value: 'age', label: 'Age' }] },
 *     { label: 'Custom', value: 'custom' },
 *   ]}
 *   value={category}
 *   onChange={setCategory}
 * />
 * ```
 *
 * @param props - {@link CascadingSelectProps}
 * @returns React component
 */
export const CascadingSelect = forwardRef<HTMLButtonElement, CascadingSelectProps>(
  (
    {
      clearable = false,
      data,
      dataTestId,
      disabled = false,
      error,
      label,
      onChange,
      placeholder,
      size,
      value,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const selectedLabel = findSelectedLabel(data, value);
    // The clear button sits in the input's section — a sibling of the trigger button in Mantine's
    // wrapper, not a descendant — so it stays valid HTML and its click never reaches the trigger.
    const showClear = clearable && !disabled && value !== null;

    return (
      <Menu
        disabled={disabled}
        middlewares={MIDDLEWARES}
        position="bottom-start"
        transitionProps={SELECT_TRANSITION_PROPS}
        width="target"
      >
        <Menu.Target>
          <InputBase
            component="button"
            type="button"
            data-testid={dataTestId}
            disabled={disabled}
            error={error}
            label={label}
            pointer
            ref={ref}
            rightSection={
              showClear ? (
                <CloseButton
                  aria-label={t('Clear selection')}
                  onClick={() => onChange(null)}
                  size="sm"
                />
              ) : (
                <FiChevronDown size={14} />
              )
            }
            rightSectionPointerEvents={showClear ? 'all' : 'none'}
            size={size}
          >
            {selectedLabel === undefined ? (
              <InputBase.Placeholder>{placeholder}</InputBase.Placeholder>
            ) : (
              /* A label wider than the input would otherwise wrap, and its second line spill past
                 the button's fixed height. `display="block"` is what makes `truncate` bite: an
                 inline box has no width to overflow, so the label would just run past the chevron.
                 `inherit` keeps the input's own font. */
              <Text display="block" inherit span truncate>
                {selectedLabel}
              </Text>
            )}
          </InputBase>
        </Menu.Target>

        <Menu.Dropdown>
          {/* One radio group across the whole tree carries the selection into every panel. */}
          {/* Scrolls here, not on the dropdown: a static box doesn't clip the sub-panels, which are
              positioned against the dropdown. */}
          <Box className={classes.list}>
            <Menu.RadioGroup onChange={onChange} value={value}>
              {data.map((entry) => {
                if (entry.items === undefined) {
                  return renderItem(entry);
                }

                // A disabled or empty group keeps its row but gets no panel: Mantine's sub-item
                // hover listeners fire even on a disabled button, so a panel that must not open
                // must not exist.
                const withPanel = !entry.disabled && entry.items.length > 0;

                return (
                  // The design floats each group's panel a step away from the list. The offset is
                  // measured from the item, which sits inside the dropdown's padding, so 12 here is
                  // ~8px of visible gap between the panels; safeAreaPolygon keeps the panel open
                  // while the cursor crosses it.
                  <Menu.Sub key={`group-${entry.label}`} offset={12} safeAreaPolygon>
                    <Menu.Sub.Target>
                      <Menu.Sub.Item disabled={entry.disabled}>{entry.label}</Menu.Sub.Item>
                    </Menu.Sub.Target>

                    {withPanel && (
                      <Menu.Sub.Dropdown>{entry.items.map(renderItem)}</Menu.Sub.Dropdown>
                    )}
                  </Menu.Sub>
                );
              })}
            </Menu.RadioGroup>
          </Box>
        </Menu.Dropdown>
      </Menu>
    );
  },
);

CascadingSelect.displayName = 'CascadingSelect';
